import { type ThreeEvent, useThree } from '@react-three/fiber';
import { memo, useCallback, useMemo } from 'react';
import { type Vector3Tuple } from 'three';

import { convertRange } from '@ubnt/wifi-calculator/utils/math';
import { useDispatch, useSelector } from 'app/store';
import { activeElementActivateLink } from 'modules/activeElement/slice';
import { Z } from 'modules/editor/constants';
import {
  linksLinkAirSpeedsSelector,
  linksLinkApIdSelector,
  linksLinkChannelWidthsSelector,
  linksLinkClientIdSelector,
  linksLinkPhyRatesMapSelector,
  linksLinkSpeedSelector,
  linksLinkVariantSelector,
} from 'modules/links/slice';
import { LinkDistance } from 'modules/editor/components/LinkDistance';
import { devicesMeshApExistsSelector } from 'modules/devices/slice';
import { settingsShowAnimationSelector, settingsUnitSelector } from 'modules/settings/slice';
import { Line, LineProps } from '../components/Line';
import { useEditorStore } from '../store';

const MCS_RANGE: [number, number] = [0, 13];
const VELOCITY_RANGE: [number, number] = [1, 7];
const CHANNEL_WIDTH_RANGE: [number, number] = [0, 40 + 240 + 320];
const WIDTH_RANGE: [number, number] = [0, 40];

interface LinkProps extends Omit<LineProps, 'id' | 'positions' | 'spreadVector' | 'velocities' | 'isActive'> {
  id: string;
  isActive: boolean;
}

const LinkContainer = (props: LinkProps) => {
  const { id, isActive, ...rest } = props;

  const scaleFactor = useEditorStore((state) => state.scaleFactor);
  const meshApExists = useSelector(devicesMeshApExistsSelector);
  const isAnimationEnabled = useSelector(settingsShowAnimationSelector);
  const unit = useSelector(settingsUnitSelector);
  const apId = useSelector((state) => linksLinkApIdSelector(state, id));
  const clientId = useSelector((state) => linksLinkClientIdSelector(state, id));
  const variant = useSelector((state) => linksLinkVariantSelector(state, id));

  const linkSpeed = useSelector((state) => linksLinkSpeedSelector(state, id, variant));
  const apPosition = useEditorStore((state) => state.devices[apId].position);
  const clientPosition = useEditorStore((state) => state.devices[clientId].position);
  const positions = useMemo(
    () =>
      [
        [apPosition[0], apPosition[1], Z.LINK],
        [clientPosition[0], clientPosition[1], Z.LINK],
      ] as [Vector3Tuple, Vector3Tuple],
    [apPosition, clientPosition]
  );

  const channelWidths = useSelector((state) => linksLinkChannelWidthsSelector(state, id));
  const airSpeeds = useSelector((state) => linksLinkAirSpeedsSelector(state, id));
  const phyRates = useSelector((state) => linksLinkPhyRatesMapSelector(state, id));

  const { width, spreadVector, velocities } = useMemo(() => {
    let totalChannelWidth = 0;
    for (let i = 0; i < channelWidths.length; i++) {
      if (airSpeeds[i]) totalChannelWidth += channelWidths[i];
    }

    const width = convertRange(totalChannelWidth, CHANNEL_WIDTH_RANGE, WIDTH_RANGE);

    let spreadVector: Vector3Tuple;
    if (totalChannelWidth === 0) {
      spreadVector = [0, 0, 0];
    } else {
      const invTotal = 1 / totalChannelWidth;
      spreadVector = [
        (airSpeeds[0] ? (channelWidths[0] ?? 0) : 0) * invTotal,
        (airSpeeds[1] ? (channelWidths[1] ?? 0) : 0) * invTotal,
        (airSpeeds[2] ? (channelWidths[2] ?? 0) : 0) * invTotal,
      ];
    }

    const velocities: Vector3Tuple = [0, 0, 0];
    if (phyRates.length === 1) {
      velocities[0] = convertRange(phyRates[0].mcs, MCS_RANGE, VELOCITY_RANGE);
    } else {
      for (const rate of phyRates) {
        const velocity = convertRange(rate.mcs, MCS_RANGE, VELOCITY_RANGE);
        const firstChar = rate.bandLabel.charCodeAt(0);
        if (firstChar === 54) { // 54 is '6'
          velocities[0] = velocity;
        } else if (firstChar === 53) { // 53 is '5'
          velocities[1] = velocity;
        } else {
          velocities[2] = velocity;
        }
      }
    }

    return { width, spreadVector, velocities };
  }, [channelWidths, airSpeeds, phyRates]);

  const isError = linkSpeed <= 0;

  const domElement = useThree((state) => state.gl.domElement);
  
  const showContextMenu = useCallback(
    (event: ThreeEvent<MouseEvent>) => {
      const store = useEditorStore.getState();
      if (!meshApExists && store.state !== 'dragging') {
        store.updateContextMenu({
          open: true,
          position: event.point.clone(),
          id,
        });
      }
    },
    [id, meshApExists]
  );
  
  const onPointerOver = useCallback(
    (event: ThreeEvent<MouseEvent>) => {
      domElement.style.cursor = 'pointer';
      useEditorStore.setState({ hoverLinkId: id });
      if (!useEditorStore.getState().contextMenu.id) {
        showContextMenu(event);
      }
    },
    [domElement, id, showContextMenu]
  );
  
  const onPointerOut = useCallback(() => {
    domElement.style.cursor = 'auto';
    useEditorStore.setState({ hoverLinkId: null });
  }, [domElement]);
  
  const onPointerMove = useCallback(
    (event: ThreeEvent<MouseEvent>) => {
      if (!meshApExists) {
        const menuPosition = useEditorStore.getState().contextMenu.position;
        if (menuPosition) {
          const { x, y } = event.point;
          const absX = Math.abs(menuPosition.x - x);
          const absY = Math.abs(menuPosition.y - y);
          if (absX > absY || menuPosition.y > y || absY / scaleFactor > 2) {
            showContextMenu(event);
          }
        }
      }
    },
    [showContextMenu, meshApExists, scaleFactor]
  );
  
  const dispatch = useDispatch();
  const onClick = useCallback(
    (event: ThreeEvent<MouseEvent>) => {
      // prevent triggering twice
      event.stopPropagation();
      showContextMenu(event);
      dispatch(activeElementActivateLink(id));
    },
    [id, showContextMenu, dispatch]
  );

  let linkDistanceOffset = Math.ceil(width / 2) + 12;
  // in MLO case we add more offset
  if (phyRates.length > 1) {
    linkDistanceOffset += 8;
  }

  return (
    <>
      <Line
        onClick={onClick}
        onPointerOver={onPointerOver}
        onPointerOut={onPointerOut}
        onPointerMove={onPointerMove}
        positions={positions}
        spreadVector={spreadVector}
        velocities={velocities}
        width={width}
        isError={isError}
        isActive={isActive}
        isAnimationEnabled={isAnimationEnabled}
        {...rest}
      />
      <LinkDistance positions={positions} isActive={isActive} offset={linkDistanceOffset} unit={unit} />
    </>
  );
};

export const Link = memo(LinkContainer);
